import { ActionPayloadValidationError, requireStringField } from "@slot/client-engine";
import type { ReplayContext, RoundAction, RoundActionHandler } from "@slot/client-engine";

/** The action's name on the wire: the hold mechanic's one action (decision 50). */
export const HOLD_CELLS_ACTION_TYPE = "hold_cells";

/** What the handler drives (the mechanic's `HoldGridPresenter`). */
export interface HoldCellsTarget {
  /** The cells the next frame of `roundIdentifier` holds: they keep their views and never roll. */
  holdCells(roundIdentifier: string, indices: ReadonlySet<number>): void;
}

/**
 * MECHANIC action `hold_cells { positionMask }` (decision 50): the cells the event's frame holds, one '0' / '1' per
 * cell in the canonical linear order. A held cell keeps its view and never rolls; every other cell rolls and lands on
 * the frame. A frame without the action holds nothing.
 *
 * SERVER-AUTHORITATIVE: what is held cannot be read off the board (a symbol looks the same whether it has just landed
 * and will roll away or is held for the rest of the feature), so the server states it and the client never infers it.
 * It must run in the frame's `frame_prepare` phase, before the frame reveals: the backend and the fixtures author it with
 * `actionPhaseName: "frame_prepare"` and never skippable (an unknown type resolves to "other", after the reveal). A
 * hold that arrives in any other phase, or a mask of the wrong length or with a character other than '0' / '1', is a
 * protocol error: the frame would roll a held symbol.
 */
export class HoldCellsActionHandler implements RoundActionHandler {
  public readonly actionTypeName = HOLD_CELLS_ACTION_TYPE;

  public constructor(
    private readonly target: HoldCellsTarget,
    private readonly cellCount: number,
  ) {}

  public async handleAction(roundAction: RoundAction, context: ReplayContext): Promise<void> {
    if (context.currentActionPhaseName !== "frame_prepare") {
      throw new ActionPayloadValidationError(this.actionTypeName, "actionPhaseName", `Must run in "frame_prepare" (before the frame it holds reveals), ran in "${context.currentActionPhaseName ?? "?"}".`);
    }
    const mask = requireStringField(this.actionTypeName, roundAction.actionPayload, "positionMask");
    if (mask.length !== this.cellCount) {
      throw new ActionPayloadValidationError(this.actionTypeName, "positionMask", `Must have one character per cell (${this.cellCount}), got ${mask.length}.`);
    }
    const indices = new Set<number>();
    for (let i = 0; i < mask.length; i++) {
      const ch = mask[i];
      if (ch !== "0" && ch !== "1") throw new ActionPayloadValidationError(this.actionTypeName, "positionMask", "Must contain only '0' and '1'.");
      if (ch === "1") indices.add(i);
    }
    this.target.holdCells(context.roundIdentifier, indices);
  }
}
